/**
 * When whole-deck chat scope hands itself back to the focused slide.
 *
 * Deck scope is a different contract from slide scope, not merely a wider one:
 * it can add, delete and reorder slides and sections, and it costs roughly 20s
 * a turn more (measured 2026-08-01). Scope is DERIVED — no focused slide means
 * deck scope — so before this it was possible to fall into whole-deck scope by
 * scrolling and then stay there, with a later "make this punchier" able to
 * restructure the deck. Whole-deck work should be chosen, never inherited.
 *
 * The rule: deck scope is sticky while its work is LIVE (a turn running, or a
 * proposal still awaiting review), and is released on the next slide change
 * after that. Re-entering it is a deliberate click on the scope chip.
 *
 * The anchor is what makes this safe around the programmatic handoffs
 * (describe-to-add, an Activity row jumping to a deck turn): those engage deck
 * scope AND move the canvas, so "the active slide changed" on its own would
 * undo them the instant they fired. Releasing only once focus has left the
 * slide deck scope was engaged ON leaves them intact.
 *
 * Pure so it can be tested without a browser — the caller owns the state.
 */
export type DeckScopeState = {
  /** Is the chat in whole-deck scope? */
  scopeCleared: boolean;
  /** Slide id focused when deck scope engaged; null/undefined before it has
   *  been taken. */
  anchor?: string | null;
  /** Currently focused slide id ('' if none). */
  activeSlideId?: string | null;
  /** A turn running, or a proposal unreviewed. */
  deckWorkLive?: boolean;
};

/**
 * clear   — not in deck scope; forget any anchor
 * anchor  — just entered deck scope; remember the current slide
 * hold    — stay in deck scope
 * release — hand the chat back to slide scope
 */
export type DeckScopeHandback = 'clear' | 'anchor' | 'hold' | 'release';

export function deckScopeHandback({
  scopeCleared,
  anchor,
  activeSlideId,
  deckWorkLive,
}: DeckScopeState): DeckScopeHandback {
  if (!scopeCleared) return 'clear';
  if (anchor === null || anchor === undefined) return 'anchor';
  // Never release mid-flight: the turn the user started is a DECK turn, and its
  // cards are reviewed in deck scope. Releasing here would re-aim the chat
  // while its own work was still on screen.
  if (deckWorkLive) return 'hold';
  return (activeSlideId ?? '') === anchor ? 'hold' : 'release';
}
